import React from "react";
// Custom Fields Drawer

// Default
import { useState } from "react";

// Mui
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Drawer from "@mui/material/Drawer";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";
import ConfirmationModal from "../modals/ConfirmationModal";

// React icons
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { RxDragHandleDots2 } from "react-icons/rx";
import { IoMdClose } from "react-icons/io";

// Utils
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { fetchPOSTRequest } from "../../utils/services";

// Redux
import { ToastState } from "../../types";
import { useAppSelector } from "../../redux/hooks";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const AddCustomFieldsDrawer = ({
  setToast,
}: {
  setToast: (toast: ToastState) => void;
}) => {
  const {
    fieldsDrawer,
    toggleCustomFieldDrawer,
    toggleCustomFieldModal,
    createdFields,
    confirmModal,
    toggleConfirmModal,
    setCreatedFields,
  } = useCustomFields();

  const { customFieldId } = useAppSelector((state) => state.auth);
  const [allFields, setAllFields] = useState();

  const generateUniqueName = () => {
    const newName = `field${new Date().getMilliseconds()}`;
    return newName;
  };

  const saveCustomFields = async () => {
    if (createdFields) {
      try {
        const savedCreatedFields = createdFields?.[fieldsDrawer?.type]?.map(
          (item: any) => {
            return { ...item, save: true };
          }
        );

        const updatedFields = {
          ...createdFields,
          [fieldsDrawer?.type]: savedCreatedFields,
        };

        const requestData = {
          id: {
            id: customFieldId,
            entityType: "ASSET",
          },
          name: generateUniqueName(),
          type: "CUSTOM_FIELD",
          additionalInfo: {
            customFields: updatedFields,
          },
        };

        if (customFieldId) {
          requestData.id = {
            id: customFieldId,
            entityType: "ASSET",
          };
        }

        const response = await fetchPOSTRequest(`asset`, requestData);

        if (response?.additionalInfo) {
          setToast({
            message: "Fields saved successfully",
            severity: "success",
          });
          setCreatedFields(updatedFields);
        }
      } catch (error: any) {
        setToast({
          message: error?.message || "Something went wrong",
          severity: "error",
        });
      } finally {
        toggleCustomFieldDrawer("", false);
      }
    } else {
      setToast({
        message: "Create custom fields",
        severity: "error",
      });
      toggleCustomFieldDrawer("", false);
    }
  };

  const handleYes = () => {
    const filterFields = createdFields?.[fieldsDrawer?.type]?.filter(
      (item: any) => item.save
    );

    const updatedFields = {
      ...createdFields,
      [fieldsDrawer?.type]: filterFields,
    };

    setCreatedFields(updatedFields);
    toggleConfirmModal(false);
    toggleCustomFieldDrawer("", false);
  };

  return (
    <>
      <Drawer
        open={fieldsDrawer?.show}
        // onClose={() => toggleCustomFieldDrawer("", true)}
        anchor="right"
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            position: "fixed",
            zIndex: 1300,
            paddingTop: "0px",
            opacity: 1,
          },
        }}
        ModalProps={{
          sx: {
            zIndex: 1400,
          },
        }}
      >
        <Box
          sx={{
            width: 350,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
          role="presentation"
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "#ffffff",
              zIndex: 2,
              borderBottom: "1px solid #D7DAE7",
              padding: "15px",
            }}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5" fontSize={"17px"}>
                Add Custom Fields
              </Typography>
              <IconButton onClick={() => toggleConfirmModal(true)}>
                <UnifiedIcon icon={IoMdClose} color="#000000" fontSize="20px" />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {createdFields?.[fieldsDrawer?.type]?.map((field: any) => {
              return (
                <Stack
                  direction={"row"}
                  gap={"10px"}
                  alignItems={"center"}
                  padding={"10px"}
                >
                  <UnifiedIcon
                    icon={RxDragHandleDots2}
                    color="#000000"
                    fontSize="20px"
                  />
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    <Typography variant="body1">{field.title}</Typography>
                    <UnifiedIcon
                      icon={MdOutlineKeyboardArrowRight}
                      color="#000000"
                      fontSize="22px"
                    />
                  </Stack>
                </Stack>
              );
            })}
          </Box>

          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#ffffff",
              zIndex: 2,
              borderTop: "1px solid #D7DAE7",
              padding: "10px",
            }}
          >
            <ContainedButton
              label={"Save Custom Field"}
              sx={{
                width: "100%",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={saveCustomFields}
            />
            <ContainedButton
              label={"Add Field"}
              sx={{
                width: "100%",
                fontWeight: 600,
                color: "#ffffff",
                mt: "15px",
              }}
              onClick={() => {
                toggleCustomFieldModal(true);
                toggleCustomFieldDrawer(fieldsDrawer?.type, false);
              }}
            />
          </Box>
        </Box>
      </Drawer>

      <ConfirmationModal
        confirmModal={confirmModal}
        onClose={() => toggleConfirmModal(false)}
        onClick={handleYes}
      />
    </>
  );
};

export default AddCustomFieldsDrawer;
